import React from 'react'
import cx from 'classnames'

import { AdoptionComponent } from '../../constants/AdoptionComponent'
import { getDsAdoptionAttribute } from '../../utils/dsAdoption'
import { getIsMobile } from '../../utils/ismobile'

import { DEFAULT_TOOLTIP_POSITION, TOOLTIP_POSITIONS_ENUM } from './constants'
import { getTipPositionData } from './getTipPositionData'
import { getTipWrapperWidth } from './getTipWrapperWidth'
import { Tip } from './TooltipComponent'

import styles from './Tooltip.module.css'

const ATTR = 'data-title'
const NS_DEFAULT = 'main'
const instances = {}
const interactiveTagNames = new Set(['button', 'a', 'input', 'textarea'])

const getInstance = (ns) => {
  return instances[ns]
}

const isInteractiveElement = (el) => {
  const tag = el.tagName?.toLowerCase()

  return interactiveTagNames.has(tag) || el.getAttribute('role') === 'button' || el.tabIndex >= 0
}

export default class TooltipWrapper extends React.Component {
  static _instance = null

  static defaultProps = {
    className: cx('animated', styles.tip),
    ns: NS_DEFAULT,
  }

  static update = (ns = NS_DEFAULT) => {
    instances[ns] && instances[ns].forceUpdate()
  }

  constructor(props, context) {
    super(props, context)
    this.state = {
      target: null,
      isMeasured: false,
    }
  }

  dropInstance = () => {
    const { ns } = this.props
    const instance = instances[ns]
    if (instance) {
      const container = document
      container.removeEventListener('mouseover', instance.handleHover, { capture: true })
      container.removeEventListener('scroll', instance.handleScroll, { capture: true })
      if (getIsMobile()) {
        container.removeEventListener('touchstart', instance.handleTouchStart, { capture: true })
      }
      instances[ns] = null
      delete instances[ns]
    }
  }

  setInstance = () => {
    this.dropInstance()
    const { ns } = this.props
    const container = document
    container.addEventListener('mouseover', this.handleHover, { capture: true })
    container.addEventListener('scroll', this.handleScroll, { capture: true })
    if (getIsMobile()) {
      container.addEventListener('touchstart', this.handleTouchStart, { capture: true })
    }
    instances[ns] = this
  }

  shouldComponentUpdate({ className }, { target, isMeasured }) {
    const { props, state } = this
    return (
      target !== state.target ||
      className !== props.className ||
      (isMeasured && isMeasured !== state.isMeasured)
    )
  }

  componentDidMount() {
    this.setInstance()
  }

  componentWillUnmount() {
    this.dropInstance()
  }

  findTip = (el) => {
    while (el) {
      if (el === document || el === this.props.container) {
        break
      }

      if (el.hasAttribute(ATTR)) {
        return el
      }

      if (el === this.tooltipRef?.current) {
        return this.state.target
      }

      el = el.parentNode
    }

    return null
  }

  getHintData = (target) => {
    const content = this.props.content || target.getAttribute(ATTR)
    const cls = target.getAttribute(`${ATTR}-cls`)
    let at = target.getAttribute(`${ATTR}-at`)
    let offsetBottom = target.getAttribute(`${ATTR}-offset-bottom`)
    const type = target.getAttribute(`${ATTR}-type`)
    const delay = Number(target.getAttribute(`${ATTR}-delay`) || 50)
    const width = target.getAttribute(`${ATTR}-width`)

    if (!(at in TOOLTIP_POSITIONS_ENUM)) {
      at = DEFAULT_TOOLTIP_POSITION
    }

    if (isNaN(Number(offsetBottom))) {
      offsetBottom = 0
    }

    return {
      content,
      cls,
      at,
      delay,
      type,
      width,
      offsetBottom: Number(offsetBottom),
    }
  }

  handleHover = ({ target }) => {
    if (getIsMobile()) return

    const nextTarget = this.findTip(target)

    this.processTarget(nextTarget)
  }

  handleTouchStart = (event) => {
    const { target } = event
    const hint = this.findTip(target)
    const tooltipNode = this.tooltipRef?.current

    const isTapInsideTooltip = tooltipNode?.contains(target)
    const isSameTarget = hint === this.state.target

    if (isTapInsideTooltip) return

    if (!hint || isSameTarget || isInteractiveElement(hint)) {
      this.processTarget(null)
    } else {
      this.processTarget(target)
    }
  }

  processTarget = (target) => {
    const instance = getInstance(this.props.ns)

    clearTimeout(instance.timeout)

    if (!instance) return

    if (!target) {
      this.setState({ target: null, isMeasured: false })
      return
    }

    const hint = this.findTip(target)

    if (!hint) return

    const { delay } = this.getHintData(hint)

    if (this.state.target && this.state.target !== hint) {
      this.setState({ target: hint, isMeasured: false })
      return
    }

    instance.timeout = setTimeout(() => {
      this.setState({ target: hint, isMeasured: false })
    }, delay)
  }

  handleScroll = () => {
    if (this.state.target) {
      this.setState({ target: null })
    }
  }

  tooltipRef = React.createRef()

  getAdoptionAttribute = () => {
    return getDsAdoptionAttribute(AdoptionComponent.TOOLTIP_WRAPPER)
  }

  handleTooltipUpdate = () => {
    this.setState({ isMeasured: true })
  }

  render = () => {
    const { className, wrapperClassName } = this.props
    const { target } = this.state

    if (!target) {
      return null
    }

    const { content, cls, at, type, width, offsetBottom } = this.getHintData(target)

    const tipPositionData = getTipPositionData({
      position: at,
      targetBoundingRect: target.getBoundingClientRect(),
      tooltipSize: {
        width: this.tooltipRef.current?.offsetWidth || 0,
        height: this.tooltipRef.current?.offsetHeight || 0,
      },
      viewportSize: { width: window.innerWidth, height: window.innerHeight },
    })

    const wrapperWidth = getTipWrapperWidth(
      tipPositionData.position,
      target.getBoundingClientRect(),
    )

    return (
      <div className={styles.screenLimits} {...this.getAdoptionAttribute()}>
        <div
          className={cx(styles.wrapper, wrapperClassName, styles[tipPositionData.position])}
          style={{
            left: tipPositionData.point.x,
            top: tipPositionData.point.y,
            width: wrapperWidth,
            '--tip-offset-x': `${tipPositionData.offset.x}px`,
            '--tip-offset-y': `${tipPositionData.offset.y}px`,
            '--tip-custom-offset-bottom': `${offsetBottom}px`,
          }}
        >
          <Tip
            ref={this.tooltipRef}
            content={content}
            width={width}
            onUpdate={this.handleTooltipUpdate}
            className={cx(
              className || styles.tip,
              styles[tipPositionData.position],
              styles[type],
              cls,
              styles.show,
            )}
          />
        </div>
      </div>
    )
  }
}
